import { localeHref } from "@/lib/locale";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Mail, ArrowRight, HeartHandshake } from "lucide-react";

export const Route = createFileRoute("/forgot-password")({
  head: () => ({
    meta: [
      { title: "Reset password — Recovery Trauma™" },
      {
        name: "description",
        content: "Reset your Recovery Trauma™ account password.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: ForgotPasswordPage,
});

const COOLDOWN_SECONDS = 60;
const STORAGE_KEY = "rt-forgot-cooldown-until";

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);

  // Rehydrate cooldown from localStorage so refreshes don't reset the timer.
  useEffect(() => {
    try {
      const until = Number(localStorage.getItem(STORAGE_KEY) || 0);
      if (until > Date.now()) {
        setCooldown(Math.ceil((until - Date.now()) / 1000));
      }
    } catch {
      /* storage unavailable */
    }
  }, []);

  // Tick the countdown every second.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => {
      setCooldown((c) => (c <= 1 ? 0 : c - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const startCooldown = () => {
    const until = Date.now() + COOLDOWN_SECONDS * 1000;
    try {
      localStorage.setItem(STORAGE_KEY, String(until));
    } catch {
      /* storage unavailable */
    }
    setCooldown(COOLDOWN_SECONDS);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (cooldown > 0 || loading) return;
    setError("");
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: localeHref("/reset-password"),
    });
    setLoading(false);
    if (error) {
      setError(error.message);
      toast.error("We couldn't send the reset link. Please try again.");
    } else {
      setSent(true);
      startCooldown();
      toast.success("Reset link sent", {
        description: `If an account exists for ${email}, a password reset email is on its way.`,
      });
    }
  };

  const disabled = loading || cooldown > 0;
  const buttonLabel = loading
    ? "Sending..."
    : cooldown > 0
      ? `Please wait ${cooldown}s`
      : sent
        ? "Resend link"
        : "Send reset link";

  return (
    <div className="flex min-h-[80vh] items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[var(--gradient-gold)]">
            <HeartHandshake className="h-6 w-6 text-foreground" />
          </div>
          <h1 className="mt-4 font-display text-2xl text-foreground">
            Reset your password
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            We'll send you a gentle link to set a new password.
          </p>
        </div>

        {sent && (
          <div className="mb-4 rounded-2xl border border-gold/30 bg-cream/60 p-5 text-center text-sm text-foreground">
            <p className="font-medium">Check your inbox</p>
            <p className="mt-1 text-muted-foreground">
              If an account exists for {email}, a reset link is on its way.
            </p>
            {cooldown > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                You can request another link in {cooldown}s.
              </p>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute start-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                className="w-full rounded-lg border border-input bg-background py-2 ps-9 pe-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          {error && (
            <p className="rounded-lg bg-destructive/10 p-2.5 text-xs text-destructive">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={disabled || !email}
            onClick={(ev) => {
              if (disabled || !email) return;
              ev.preventDefault();
              if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
              }
              handleSubmit(ev as unknown as React.FormEvent);
            }}
            className="btn-gold relative z-20 min-h-[48px] w-full touch-manipulation justify-center disabled:opacity-60"
          >
            {buttonLabel}
            {!loading && cooldown === 0 && <ArrowRight className="h-4 w-4" />}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Remembered it?{" "}
          <Link to="/login" className="font-medium text-primary hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
